/**
 * /equipment/radiation/dosimetry — personal dose records (fieldiq-api#1334,
 * RAD-1). An admin or office staff records, edits and deletes a reading; every
 * other role is sent ONLY their own rows by the API, so the list needs no
 * filtering here.
 *
 * Doses arrive in the tenant's unit (`meta.units.dose`) and are entered in it.
 * "Over investigation level" is the API's flag; null means the company has set
 * no level, and reads `—`, never a guessed "no".
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useCreateRadDose, useDeleteRadDose, useRadCatalog, useRadDosimetry, useUpdateRadDose } from '../../hooks/useRadiation';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { RAD_DOSIMETER_LABEL, canAdminRad, radDay, radErrorMessage, radLabel, radQty } from '../../lib/radiation';
import type { RadDose, RadDoseWrite } from '../../types/radiation';
import { RAD_ALERT, RadSectionNav, useRadPeople } from '../../components/radiation/RadBits';
import { DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel, useOpenCount } from '../downhole/DownholeBits';

const ALLOWED = 'admins, office staff, supervisors, dispatchers, field workers and logistics';
const DAY = /^\d{4}-\d{2}-\d{2}$/;

interface DoseDraft {
  user_id: string;
  period_start: string;
  period_end: string;
  dosimeter_kind: string;
  dosimeter_serial: string;
  deep_dose: string;
  shallow_dose: string;
  notes: string;
}

const draftOf = (d: RadDose | null): DoseDraft => ({
  user_id: d?.userId ?? '',
  period_start: d?.periodStart ?? '',
  period_end: d?.periodEnd ?? '',
  dosimeter_kind: d?.dosimeterKind ?? '',
  dosimeter_serial: d?.dosimeterSerial ?? '',
  deep_dose: d?.deepDose == null ? '' : String(d.deepDose),
  shallow_dose: d?.shallowDose == null ? '' : String(d.shallowDose),
  notes: d?.notes ?? '',
});

/** The body to send, or an error sentence. Validation is a courtesy; the API decides. */
export function radDoseBody(draft: DoseDraft, unit: string): RadDoseWrite | string {
  if (!draft.user_id) return 'Choose the person.';
  if (!DAY.test(draft.period_start) || !DAY.test(draft.period_end)) return 'Enter the wear period start and end.';
  if (draft.period_end < draft.period_start) return 'The period cannot end before it starts.';
  if (!draft.dosimeter_kind) return 'Choose the dosimeter kind.';
  const deep = Number(draft.deep_dose.trim());
  if (draft.deep_dose.trim() === '' || !Number.isFinite(deep) || deep < 0) return `Enter the deep dose in ${unit || 'the tenant unit'}, 0 or more.`;
  let shallow: number | null = null;
  if (draft.shallow_dose.trim() !== '') {
    shallow = Number(draft.shallow_dose.trim());
    if (!Number.isFinite(shallow) || shallow < 0) return `The shallow dose must be a number of at least 0 (${unit || 'tenant unit'}).`;
  }
  return {
    user_id: draft.user_id,
    period_start: draft.period_start,
    period_end: draft.period_end,
    dosimeter_kind: draft.dosimeter_kind,
    dosimeter_serial: draft.dosimeter_serial.trim() || null,
    deep_dose: deep,
    shallow_dose: shallow,
    notes: draft.notes.trim() || null,
  };
}

function DoseDialog({
  open,
  dose,
  kinds,
  unit,
  onClose,
}: {
  open: boolean;
  dose: RadDose | null;
  kinds: string[];
  unit: string;
  onClose: () => void;
}) {
  const openCount = useOpenCount(open);
  const create = useCreateRadDose();
  const update = useUpdateRadDose();
  const people = useRadPeople(open);
  const [draft, setDraft] = useState<DoseDraft>(draftOf(dose));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setDraft(draftOf(dose));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);
  const set = (k: keyof DoseDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setDraft((d) => ({ ...d, [k]: e.target.value }));
  const pending = create.isPending || update.isPending;

  const submit = async () => {
    setError(null);
    const body = radDoseBody(draft, unit);
    if (typeof body === 'string') return setError(body);
    try {
      if (dose) await update.mutateAsync({ id: dose.id, body });
      else await create.mutateAsync(body);
      toast.success(dose ? 'Dose record saved.' : 'Dose recorded.');
      onClose();
    } catch (e) {
      setError(radErrorMessage(e, 'The dose record could not be saved.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title={dose ? 'Edit dose record' : 'Record a dose'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" onClick={onClose} className={DH_SECONDARY}>Cancel</button>
          <button type="button" onClick={submit} disabled={pending} className="fi-action fi-action--standard fi-action--primary">
            {pending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <DhField label="Person">
          <select aria-label="Person" className={DH_INPUT} value={draft.user_id} onChange={set('user_id')}>
            <option value="">{people.isLoading ? 'Loading…' : 'Choose…'}</option>
            {/* The person on an existing record stays listed even if they have since left. */}
            {dose?.userId && !(people.data ?? []).some((p) => p.id === dose.userId) && (
              <option value={dose.userId}>{dose.userName ?? '—'}</option>
            )}
            {(people.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </DhField>
        <DhField label="Dosimeter kind">
          <select aria-label="Dosimeter kind" className={DH_INPUT} value={draft.dosimeter_kind} onChange={set('dosimeter_kind')}>
            <option value="">Choose…</option>
            {kinds.map((k) => <option key={k} value={k}>{radLabel(RAD_DOSIMETER_LABEL, k)}</option>)}
          </select>
        </DhField>
        <DhField label="Period start">
          <input aria-label="Period start" type="date" className={DH_INPUT} value={draft.period_start} onChange={set('period_start')} />
        </DhField>
        <DhField label="Period end">
          <input aria-label="Period end" type="date" className={DH_INPUT} value={draft.period_end} onChange={set('period_end')} />
        </DhField>
        <DhField label={`Deep dose (${unit || '—'})`}>
          <input aria-label="Deep dose" inputMode="decimal" className={DH_INPUT} value={draft.deep_dose} onChange={set('deep_dose')} />
        </DhField>
        <DhField label={`Shallow dose (${unit || '—'})`}>
          <input aria-label="Shallow dose" inputMode="decimal" className={DH_INPUT} value={draft.shallow_dose} onChange={set('shallow_dose')} />
        </DhField>
        <DhField label="Dosimeter serial">
          <input aria-label="Dosimeter serial" className={DH_INPUT} value={draft.dosimeter_serial} onChange={set('dosimeter_serial')} />
        </DhField>
      </div>
      <div className="mt-4">
        <DhField label="Notes">
          <textarea aria-label="Notes" rows={2} className={`${DH_INPUT} h-auto py-2`} value={draft.notes} onChange={set('notes')} />
        </DhField>
      </div>
      {error && <p role="alert" className={`mt-3 ${RAD_ALERT}`}>{error}</p>}
    </DhDialog>
  );
}

const overText = (v: boolean | null) => (v === null ? '—' : v ? 'Over' : 'Under');

export default function RadiationDosimetryScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canAdmin = canAdminRad(user?.role, elevated);
  const seesAll = user?.role === 'admin' || user?.role === 'office_staff';
  const query = useRadDosimetry();
  const catalogQuery = useRadCatalog();
  const remove = useDeleteRadDose();
  const failure = queryLoadFailure(query);
  const rows = query.data?.rows ?? [];
  const unit = query.data?.meta?.units.dose ?? '';
  const kinds = catalogQuery.data?.dosimeterKinds ?? Object.keys(RAD_DOSIMETER_LABEL);
  const [editing, setEditing] = useState<RadDose | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<RadDose | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const doDelete = async () => {
    if (!deleting) return;
    setDeleteError(null);
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Dose record deleted.');
      setDeleting(null);
    } catch (e) {
      setDeleteError(radErrorMessage(e, 'The dose record could not be deleted.'));
    }
  };

  return (
    <div data-testid="radiation-dosimetry-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Equipment"
        title="Dosimetry"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdmin ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Record dose
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <RadSectionNav />
          <DhPanel
            eyebrow="Personal dose"
            // GET /dosimetry returns every row only to the LITERAL office roles
            // (RAD_DOSE_OFFICE_ROLES); an elevated dispatcher may record doses but
            // is sent only their own, so the title follows the read, not the write.
            title={seesAll ? 'Dose records' : 'Your dose records'}
            testId="rad-dosimetry"
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading dose records" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="dose records" allowed={ALLOWED} />
            ) : rows.length === 0 ? (
              <DhEmpty>No dose is recorded yet.</DhEmpty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                    <tr>
                      <th scope="col" className="px-4 py-3">Person</th>
                      <th scope="col" className="px-4 py-3">Period</th>
                      <th scope="col" className="px-4 py-3">Dosimeter</th>
                      <th scope="col" className="px-4 py-3">Deep</th>
                      <th scope="col" className="px-4 py-3">Shallow</th>
                      <th scope="col" className="px-4 py-3">Investigation level</th>
                      {canAdmin && <th scope="col" className="px-4 py-3" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.id} className="border-t border-fieldiq-border text-fieldiq-text" data-testid="rad-dose-row">
                        <td className="px-4 py-3 font-bold text-white">{d.userName ?? '—'}</td>
                        <td className="px-4 py-3">{radDay(d.periodStart)} – {radDay(d.periodEnd)}</td>
                        <td className="px-4 py-3">
                          {radLabel(RAD_DOSIMETER_LABEL, d.dosimeterKind)}
                          {d.dosimeterSerial && <span className="ml-1 text-xs text-fieldiq-muted">{d.dosimeterSerial}</span>}
                        </td>
                        <td className="px-4 py-3 font-mono tabular-nums">{radQty(d.deepDose, unit)}</td>
                        <td className="px-4 py-3 font-mono tabular-nums">{radQty(d.shallowDose, unit)}</td>
                        <td className={`px-4 py-3 ${d.exceedsInvestigationLevel ? 'font-black underline decoration-fi-signal-red decoration-2' : ''}`} data-testid="rad-dose-over">
                          {overText(d.exceedsInvestigationLevel)}
                        </td>
                        {canAdmin && (
                          <td className="px-4 py-3">
                            <span className="flex justify-end gap-2">
                              <button type="button" aria-label="Edit dose record" onClick={() => setEditing(d)} className={DH_SECONDARY}>
                                <Pencil className="size-4" aria-hidden="true" />
                              </button>
                              <button type="button" aria-label="Delete dose record" onClick={() => { setDeleteError(null); setDeleting(d); }} className={DH_SECONDARY}>
                                <Trash2 className="size-4" aria-hidden="true" />
                              </button>
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DhPanel>
        </div>
      </section>

      {canAdmin && (
        <>
          <DoseDialog open={adding || !!editing} dose={editing} kinds={kinds} unit={unit} onClose={() => { setAdding(false); setEditing(null); }} />
          <DhDialog
            open={!!deleting}
            title="Delete this dose record?"
            onClose={() => setDeleting(null)}
            footer={
              <>
                <button type="button" onClick={() => setDeleting(null)} className={DH_SECONDARY}>Cancel</button>
                <button type="button" onClick={doDelete} disabled={remove.isPending} className="fi-action fi-action--standard fi-action--primary">
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </>
            }
          >
            <p className="text-sm text-fieldiq-text">
              {deleting?.userName ?? '—'}, {radDay(deleting?.periodStart)} – {radDay(deleting?.periodEnd)}.
            </p>
            {deleteError && <p role="alert" className={`mt-3 ${RAD_ALERT}`}>{deleteError}</p>}
          </DhDialog>
        </>
      )}
    </div>
  );
}
